// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

import type { FieldLocation } from '@bda/shared';

/** Displayed page size in CSS px. */
export interface PageSize {
  width: number;
  height: number;
}

export interface LaidOutAnnotation {
  key: string;
  box: { x: number; y: number; w: number; h: number };
  label: { x: number; y: number; maxWidth: number; placement: 'above' | 'below' };
}

/** Smallest box edge drawn, in CSS px (Req 6.14). */
export const MIN_BOX_PX = 4;

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

/**
 * One box dimension: `start` and `size` from the fraction, unrounded. A size under MIN_BOX_PX grows to it around the
 * same center, then shifts to stay within `0..extent`. A page smaller than the minimum caps the size at the page.
 */
function dimension(fracStart: number, fracSize: number, extent: number): [number, number] {
  const start = fracStart * extent;
  const size = fracSize * extent;
  if (size >= MIN_BOX_PX) return [start, size];
  const grown = Math.min(MIN_BOX_PX, extent);
  const center = start + size / 2;
  return [clamp(center - grown / 2, 0, extent - grown), grown];
}

/**
 * Lays out Annotation boxes and labels in CSS px over a page of `size`. Labels go above the box when there is room for
 * `labelHeight`, else below, and are kept inside the page. The result is sorted by box top, then left (the keyboard
 * order of Req 6.12). `measure` returns a label's rendered width in CSS px.
 */
export function layoutAnnotations(
  annotations: Array<{ key: string; location: FieldLocation; text: string }>,
  size: PageSize,
  opts: { labelHeight: number; measure: (text: string) => number },
): LaidOutAnnotation[] {
  const { width, height } = size;
  const { labelHeight, measure } = opts;
  const laidOut = annotations.map(({ key, location, text }): LaidOutAnnotation => {
    const [x, w] = dimension(location.left, location.width, width);
    const [y, h] = dimension(location.top, location.height, height);
    const placement = y >= labelHeight ? 'above' : 'below';
    const labelY = placement === 'above' ? y - labelHeight : Math.max(0, Math.min(y + h, height - labelHeight));
    const labelWidth = Math.min(Math.max(measure(text), 0), width);
    return {
      key,
      box: { x, y, w, h },
      label: { x: clamp(x, 0, width - labelWidth), y: labelY, maxWidth: width, placement },
    };
  });
  return laidOut.sort((a, b) => a.box.y - b.box.y || a.box.x - b.box.x);
}
